import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import slugify from "../utils/slugify";
import ReactPlayer from "react-player";
// removed unused icon import
import { AiFillLike } from "react-icons/ai";
import { FiShare2, FiX } from "react-icons/fi";
import { FaEye } from "react-icons/fa";
import { MdBookmark, MdBookmarkBorder } from "react-icons/md";
import { abbreviateNumber } from "js-abbreviation-number";

import { DataContext } from "../context/contextApi";
import { Helmet } from "react-helmet-async";
import SuggestionVideoCard from "../components/SuggestionVideoCard";
import VideoCard from "../components/VideoCard";
import ShimmerSuggestionList from "../shared/ShimmerSuggestionList";
import pndBanner from "../assets/d33c4227-8938-4ea6-a2f5-065528230c96.png";
import LazyImage from "../components/LazyImage";
import { incrementView, changeLikes } from "../firebase";
import { addToFavorites, removeFromFavorites, isFavorited } from "../utils/favorites";
import { toAppVideo, deriveTitleFromUrl } from "../utils/videoHelper";

const VideoDetails = () => {
  const [video, setVideo] = useState();
  const [relatedVideos, setRelatedVideos] = useState("");
  const { slug } = useParams();
  const location = useLocation();
  const [resolvedId, setResolvedId] = useState(null);
  const { setLoading, firebaseVideos, firebaseLoading } = useContext(DataContext);
  // liked state removed (not displayed in UI, only stored in localStorage)
  const [likesCount, setLikesCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [likeProcessing, setLikeProcessing] = useState(false);
  const playerRef = useRef(null);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  // start unmuted by default per user request; we'll attempt programmatic play and fall back to muted autoplay if blocked
  const [isMuted, setIsMuted] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(true);

  // resolve slug -> videoId (try exact id match, then by title slug)
  useEffect(() => {
    document.getElementById("root").classList.add("custom-h");
    // If the route was navigated to with a video object in state (e.g. from deep search),
    // prefer that video and avoid trying to resolve via Firebase.
    if (location && location.state && location.state.playerVideo) {
      try {
        console.log('[VideoDetails] Received playerVideo from route state:', location.state.playerVideo);
        const normalized = toAppVideo(location.state.playerVideo);
        console.log('[VideoDetails] Normalized playerVideo:', normalized);
        if (normalized && normalized.videoId) {
          setResolvedId(normalized.videoId);
          setVideo(normalized);
          return; // skip the firebase-based resolution
        }
      } catch (e) {
        // fall through to normal resolution if normalization fails
        console.warn('Failed to normalize playerVideo from route state', e);
      }
    }

    let foundId = null;
    if (firebaseVideos && firebaseVideos.length > 0) {
      // try direct videoId match
      const direct = firebaseVideos.find((v) => v.videoId === slug);
      if (direct) foundId = direct.videoId;
      else {
        // try slugified title match
        const byTitle = firebaseVideos.find((v) => slugify(v.title || v.titleText || "") === slug);
        if (byTitle) foundId = byTitle.videoId;
      }
    }

    // fallback: treat slug as id
    if (!foundId) foundId = slug;

    setResolvedId(foundId);
  }, [slug, firebaseVideos, location?.state?.playerVideo, location]);

  // update local likes/views state when firebaseVideos change
  useEffect(() => {
    if (!firebaseVideos || firebaseVideos.length === 0 || !resolvedId) return;
    const found = firebaseVideos.find((v) => v.videoId === resolvedId);
    if (found) {
      setLikesCount(found?.stats?.likes || 0);
      setViewsCount(found?.stats?.views || 0);
    }
    // set favorited flag from localStorage
    setFavorited(isFavorited(resolvedId));
  }, [firebaseVideos, resolvedId]);

  // once we have a resolvedId, fetch details and related videos
  useEffect(() => {
    if (!resolvedId) return;

    // fetch Data for that perticular video from Firebase
    const fetchVideoDetails = async () => {
      if (!resolvedId) return;
      
      // Don't set loading if Firebase is still loading initial data
      if (!firebaseLoading) {
        setLoading(true);
      }
      
      try {
        // Wait for Firebase data to load
        if (firebaseLoading) {
          // Don't clear video if it was set from route state (deep search)
          if (!location?.state?.playerVideo) {
            setVideo(undefined);
          }
          return;
        }

        // Get video details from Firebase instead of API
        if (!firebaseVideos || firebaseVideos.length === 0) {
          // Don't clear video if it was set from route state (deep search)
          if (!location?.state?.playerVideo) {
            setVideo(undefined);
          }
          return;
        }

        const foundVideo = firebaseVideos.find((v) => v.videoId === resolvedId);
        if (foundVideo) {
          // Keep the full mapped video object (includes originalUrl and platform)
          setVideo(foundVideo);
        } else {
          // Don't clear video if it was set from route state (deep search)
          // This preserves Facebook videos from deep search that aren't in Firebase
          if (!location?.state?.playerVideo) {
            setVideo(undefined);
          }
        }
      } catch (e) {
        console.error("fetchVideoDetails error:", e);
        // Don't clear video on error if it was set from route state
        if (!location?.state?.playerVideo) {
          setVideo(undefined);
        }
      } finally {
        if (!firebaseLoading) {
          setLoading(false);
        }
      }
    };

    // fetch Data for the suggestion videos from Firebase
    const fetchRelatedVideos = async () => {
      if (!resolvedId) return;
      
      // Don't set loading if Firebase is still loading initial data
      if (!firebaseLoading) {
        setLoading(true);
      }
      
      try {
        // Wait for Firebase data to load
        if (firebaseLoading) {
          setRelatedVideos([]);
          return;
        }

        // Get related videos from Firebase instead of API
        if (!firebaseVideos || firebaseVideos.length === 0) {
          setRelatedVideos([]);
          return;
        }
        // Find the current video to get its tag/category and author
        const currentVideo = firebaseVideos.find((v) => v.videoId === resolvedId);
        const currentTag = currentVideo?.customTag;

        // Candidates exclude current
        const candidates = firebaseVideos.filter((v) => v.videoId !== resolvedId);

        // Scoring algorithm: prefer same tag, then same author, then popular/recent, with some randomness
        const scored = candidates.map((v) => {
          let score = 0;
          if (currentTag && v.customTag === currentTag) score += 50;
          if (
            currentVideo?.author?.title &&
            v?.author?.title &&
            v.author.title === currentVideo.author.title
          )
            score += 30;

          const views = v?.stats?.views || 0;
          // add scaled view score (logarithmic)
          score += Math.min(20, Math.log10(views + 1) * 5);

          // recency heuristic from publishedTimeText (best-effort)
          let recencyScore = 0;
          const pt = (v?.publishedTimeText || "").toLowerCase();
          const m = pt.match(/(\d+)\s*(second|minute|hour|day|week|month|year)/i);
          if (m) {
            const n = parseInt(m[1], 10);
            const unit = m[2].toLowerCase();
            let weight = 1;
            if (unit.startsWith("second") || unit.startsWith("minute") || unit.startsWith("hour")) weight = 10;
            else if (unit.startsWith("day")) weight = 8;
            else if (unit.startsWith("week")) weight = 6;
            else if (unit.startsWith("month")) weight = 4;
            else weight = 2;
            recencyScore = Math.max(0, 10 - Math.log10(n + 1)) * (weight / 10);
          }
          score += recencyScore;

          // small random jitter to diversify results
          score += Math.random();

          return { video: v, score };
        });

        // sort by score desc and take top 15
        scored.sort((a, b) => b.score - a.score);
        const related = scored.slice(0, 15).map((s) => ({ type: "video", video: s.video }));

        setRelatedVideos(related);
      } catch (e) {
        console.error("fetchRelatedVideos error:", e);
        setRelatedVideos([]);
      } finally {
        if (!firebaseLoading) {
          setLoading(false);
        }
      }
    };

    fetchVideoDetails();
    fetchRelatedVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, firebaseVideos, firebaseLoading, setLoading]);

  // increment view count only once when video is first opened
  useEffect(() => {
    if (!resolvedId) return;
    (async () => {
      try {
        await incrementView(resolvedId);
        setViewsCount(prev => prev + 1);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [resolvedId]);

  // Try to programmatically unmute & play the video when the player and resolvedId are ready.
  // Note: browsers may still block autoplay with sound; if blocked we fall back to muted autoplay.
  useEffect(() => {
    if (!resolvedId || !playerRef.current) return;

    const attemptUnmutedPlay = async () => {
      try {
        // Optimistically set unmuted state
        setIsMuted(false);

        const internal = playerRef.current.getInternalPlayer && playerRef.current.getInternalPlayer();
        if (internal) {
          // YouTube iframe API exposes unMute and playVideo
          if (typeof internal.unMute === 'function') {
            try { internal.unMute(); } catch (e) { /* ignore */ }
          }

          if (typeof internal.playVideo === 'function') {
            try { internal.playVideo(); } catch (e) { /* ignore */ }
          } else if (typeof internal.play === 'function') {
            // HTMLMediaElement style
            await internal.play();
          }
        } else if (playerRef.current && typeof playerRef.current.play === 'function') {
          // Fallback: attempt to call play on the ref
          await playerRef.current.play();
        }
      } catch (err) {
        // Autoplay with sound blocked — fallback to muted autoplay so video still starts
        console.warn('Autoplay with sound was blocked by the browser. Falling back to muted autoplay.', err);
        setIsMuted(true);
      }
    };

    attemptUnmutedPlay();
    // We only want to attempt when resolvedId/playerRef changes
  }, [resolvedId]);

  // Ensure the loader remains until the iframe's own content has loaded.
  // Some players (YouTube via react-player) may create the iframe asynchronously,
  // so we attempt to find it and attach a 'load' event. Retry briefly if not found.
  useEffect(() => {
    if (!resolvedId || !playerLoading) return;

    let cancelled = false;
    let attempts = 0;
    let attached = false;
    let cleanupFn = null;

    const attachToIframe = () => {
      attempts += 1;
      try {
        // First try internal player from react-player
        const internal = playerRef.current && playerRef.current.getInternalPlayer && playerRef.current.getInternalPlayer();
        let iframeEl = null;

        if (internal && internal.tagName === 'IFRAME') {
          iframeEl = internal;
        } else {
          // Fallback: search for an iframe that looks like the player iframe
          const iframes = Array.from(document.querySelectorAll('iframe'));
          iframeEl = iframes.find(f => {
            try {
              const s = f.src || '';
              return s.includes(resolvedId) || s.includes('youtube-nocookie.com') || s.includes('facebook.com');
            } catch (e) { return false; }
          });
        }

        if (iframeEl) {
          const onLoad = () => {
            if (!cancelled) setPlayerLoading(false);
          };
          iframeEl.addEventListener('load', onLoad);
          attached = true;
          cleanupFn = () => iframeEl.removeEventListener('load', onLoad);
          return;
        }
      } catch (e) {
        // ignore and retry
      }

      if (!attached && attempts < 10 && !cancelled) {
        // retry after a short delay
        setTimeout(attachToIframe, 250);
      } else if (!attached && !cancelled) {
        // give up after retries and hide loader to avoid stuck state
        setPlayerLoading(false);
      }
    };

    attachToIframe();

    return () => {
      cancelled = true;
      if (cleanupFn) cleanupFn();
    };
  }, [resolvedId, playerLoading]);

  // Listen for postMessage events from embedded players (YouTube/Facebook).
  // Some providers post messages when the player is ready or changes state —
  // use these to hide the loader even when onReady/onPlay don't fire due to autoplay policies.
  useEffect(() => {
    const onMessage = (e) => {
      try {
        const origin = e.origin || '';
        let data = e.data;
        if (!data) return;
        // data can be a JSON string sometimes
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (_) { /* not JSON, continue */ }
        }

        // Common YouTube message patterns: {"event":"onReady"} or {"event":"onStateChange","info":1}
        if (data && (data.event === 'onReady' || (data.event === 'onStateChange' && (data.info === 1 || data.info === '1')) || data.playerState === 1)) {
          setPlayerLoading(false);
          return;
        }

        // Some providers send simple strings like 'ready'
        if (typeof data === 'string' && (data.toLowerCase().includes('ready') || data.toLowerCase().includes('playing'))) {
          setPlayerLoading(false);
          return;
        }

        // If message comes from known video hosts, hide loader on any message to avoid stuck loader
        if (origin.includes('youtube') || origin.includes('youtu.be') || origin.includes('facebook')) {
          setPlayerLoading(false);
        }
      } catch (err) {
        // ignore
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    setPlayerLoading(true);
  }, [resolvedId]);

  const shareUrl = `https://a2zanimes.online/${slug || resolvedId || ""}`;
  const [displayTitle, setDisplayTitle] = useState("");

  // Compute and attempt to enrich a display title with fallbacks for deep-search (non-RTDB) videos
  useEffect(() => {
    if (!video && !resolvedId) {
      setDisplayTitle("(No title)");
      return;
    }
    const looksLikeId = (s) => typeof s === 'string' && /^[A-Za-z0-9_-]{8,}$/.test(s);
    const looksLikeEncodedUrl = (s) => typeof s === 'string' && (s.indexOf('%3A') !== -1 || s.indexOf('%2F') !== -1);

    let base = video?.title || '';
    
    // Check if the title is valid (not just an ID, URL, or encoded URL)
    const isValidTitle = (t) => {
      if (!t || typeof t !== 'string') return false;
      const trimmed = t.trim();
      if (trimmed.length < 3) return false;
      if (/^\d+$/.test(trimmed)) return false; // Just numbers
      if (looksLikeId(trimmed)) return false;
      if (looksLikeEncodedUrl(trimmed)) return false;
      if (/^https?:\/\//i.test(trimmed)) return false;
      return true;
    };
    
    if (!isValidTitle(base)) {
      base = deriveTitleFromUrl(video?.originalUrl) || resolvedId || "(No title)";
    }

    // If base looks percent-encoded (e.g., 'https%3A%2F%2F...') try to decode first.
    try {
      if (typeof base === 'string' && base.indexOf('%3A') !== -1) {
        try { base = decodeURIComponent(base); } catch (e) { /* ignore decode errors */ }
      }
    } catch (e) { /* ignore */ }

    // If base is still a URL (or looks like one), prefer deriving a friendly title from the URL
    if (typeof base === 'string' && (/^https?:\/\//i.test(base) || base.toLowerCase().includes('facebook.com'))) {
      try {
        const derived = deriveTitleFromUrl(video?.originalUrl || base);
        if (derived && derived.length > 0) base = derived;
      } catch (e) { /* ignore */ }
    }

    // If base looks like a raw id (e.g., YouTube 11-char id), show a clearer fallback label
    let finalBase = base;
    if (looksLikeId(base) || /^\d+$/.test(base)) {
      if (video?.platform === 'youtube' || (video?.originalUrl && video.originalUrl.includes('youtube')) || (resolvedId && resolvedId.length === 11)) {
        finalBase = `YouTube video ${resolvedId || base}`;
      } else if (video?.platform === 'dailymotion' || (video?.originalUrl && video.originalUrl.includes('dailymotion'))) {
        finalBase = `Dailymotion video ${resolvedId || base}`;
      } else if (video?.platform === 'facebook' || (video?.originalUrl && video.originalUrl.includes('facebook'))) {
        finalBase = `Facebook Video`;
      }
    }
    setDisplayTitle(finalBase);

    // If the base title looks like an id or generic hostname, try fetching oEmbed/title from providers
    const needsFetch = looksLikeId(base) || /^\d+$/.test(base) || finalBase.startsWith('YouTube video') || finalBase.startsWith('Dailymotion video') || finalBase === 'Facebook Video' || /^[\w.-]+\.[a-z]{2,}$/i.test(base);

    let cancelled = false;
    (async () => {
      if (!needsFetch) return;
      try {
        // Try YouTube oEmbed if possible
        const tryYoutube = async () => {
          const vid = video?.videoId || resolvedId;
          if (!vid) return null;
          const oembed = `https://www.youtube.com/oembed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + vid)}&format=json`;
          const resp = await fetch(oembed);
          if (!resp.ok) return null;
          const data = await resp.json();
          return data?.title || null;
        };

        const tryDailymotion = async () => {
          const vid = video?.videoId || resolvedId;
          if (!vid) return null;
          const url = video?.originalUrl || `https://www.dailymotion.com/video/${vid}`;
          const oembed = `https://www.dailymotion.com/services/oembed?url=${encodeURIComponent(url)}&format=json`;
          const resp = await fetch(oembed);
          if (!resp.ok) return null;
          const data = await resp.json();
          return data?.title || null;
        };

        const tryFacebook = async () => {
          const url = video?.originalUrl;
          if (!url) return null;
          const oembed = `https://www.facebook.com/plugins/video/oembed.json/?url=${encodeURIComponent(url)}`;
          const resp = await fetch(oembed);
          if (!resp.ok) return null;
          const data = await resp.json();
          return data?.title || null;
        };

        let title = null;
        // Try YouTube first for obvious youtube platform
        if (video?.platform === 'youtube' || (video?.originalUrl && video.originalUrl.includes('youtube'))) {
          title = await tryYoutube();
        }
        if (!title && (video?.platform === 'dailymotion' || (video?.originalUrl && video.originalUrl.includes('dailymotion')))) {
          title = await tryDailymotion();
        }
        if (!title && (video?.platform === 'facebook' || (video?.originalUrl && video.originalUrl.includes('facebook.com')))) {
          title = await tryFacebook();
        }

        if (!title) {
          // Try generic attempts for known providers as a last resort
          try {
            const vid = video?.videoId || resolvedId;
            if (vid && !title) title = await tryYoutube();
            if (!title && video?.originalUrl) {
              // attempt to fetch og:title from the page (may be blocked by CORS)
              try {
                const resp = await fetch(video.originalUrl, { method: 'GET' });
                if (resp.ok) {
                  const txt = await resp.text();
                  const m = txt.match(/<title>([^<]+)<\/title>/i);
                  if (m && m[1]) title = m[1].trim();
                }
              } catch (e) { /* ignore CORS failures */ }
            }
          } catch (e) { /* ignore */ }
        }

        if (!cancelled && title) setDisplayTitle(title);
      } catch (e) {
        // ignore fetch errors
      }
    })();

    return () => { cancelled = true; };
  }, [video, resolvedId]);

  // Determine whether the current resolvedId corresponds to a video stored in RTDB
  const isRtdbVideo = !!(firebaseVideos && firebaseVideos.find((v) => v.videoId === resolvedId));

  return (
    <div className="flex flex-row justify-center flex-1 bg-white dark:bg-black">
      <Helmet>
        <title>{(() => {
          const metaVideo = firebaseVideos && firebaseVideos.length > 0
            ? firebaseVideos.find((v) => v.videoId === resolvedId) || video
            : video;
          return metaVideo?.title || displayTitle || "A2Z anime & Cartoons";
        })()}</title>
        <meta name="description" content={(() => {
          const metaVideo = firebaseVideos && firebaseVideos.length > 0
            ? firebaseVideos.find((v) => v.videoId === resolvedId) || video
            : video;
          const metaTitle = metaVideo?.title || displayTitle || "A2Z anime & Cartoons";
          return metaVideo?.descriptionSnippet || metaVideo?.description || `${metaTitle} - Watch anime and cartoon videos.`;
        })()} />
        {(() => {
          const metaVideo = firebaseVideos && firebaseVideos.length > 0
            ? firebaseVideos.find((v) => v.videoId === resolvedId) || video
            : video;
          const metaImageRaw = metaVideo?.thumbnails?.[0]?.url || '';
          const finalMetaImage = (metaImageRaw && /^https?:\/\//i.test(metaImageRaw))
            ? metaImageRaw
            : (window.location.origin + (metaImageRaw || '/default-thumbnail.svg'));
          return <meta property="og:image" content={finalMetaImage} />;
        })()}
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>
      <div className="w-full max-w-[1280px] flex flex-col lg:flex-row h-full">
        <div className="flex flex-col lg:w-[calc(100%-350px)] xl:w-[calc(100%-400px)] lg:px-4 lg:py-6 overflow-y-auto h-full">
          {/* Video Player Container - Edge to edge on mobile, better mobile height */}
          <div className="relative w-full overflow-hidden lg:mx-auto lg:rounded-xl"
               style={{
                 aspectRatio: (video?.width && video?.height) ? `${video.width}/${video.height}` : '16/9',
                 backgroundColor: '#000',
                 maxWidth: '100%',
                 minHeight: '200px'  // Minimum height for better mobile viewing
               }}>
              {(() => {
                // Prefer the resolved `video` object when available (from RTDB mapping).
                let currentVideo = video || (firebaseVideos && firebaseVideos.find((v) => v.videoId === resolvedId));

                // If the source is a Facebook URL, render the FB embed iframe directly.
                // Normalize and prefer explicit embed src when available.
                const decodeSafe = (s) => {
                  try {
                    if (!s) return s;
                    if (s.indexOf('%3A') !== -1 || s.indexOf('%2F') !== -1) {
                      const d = decodeURIComponent(s);
                      if (d && d.startsWith('http')) return d;
                    }
                  } catch (e) {}
                  return s;
                };

                // Check if this is a Facebook video by looking at multiple fields
                const isFacebookVideo = (v) => {
                  if (!v) return false;
                  if (v.platform === 'facebook' || v.source === 'facebook') return true;
                  const urlToCheck = v.originalUrl || v.url || '';
                  if (urlToCheck.includes('facebook.com') || urlToCheck.includes('fb.watch')) return true;
                  if (v.embed && v.embed.includes('facebook.com/plugins/video')) return true;
                  if (v.pluginUrl && v.pluginUrl.includes('facebook.com/plugins/video')) return true;
                  return false;
                };

                // If resolvedId looks like an encoded URL and we don't have originalUrl, decode it for use
                if ((!currentVideo || !currentVideo.originalUrl) && resolvedId && resolvedId.indexOf('%3A') !== -1) {
                  try {
                    const dec = decodeURIComponent(resolvedId);
                    if (!currentVideo) currentVideo = { originalUrl: dec, platform: 'facebook', title: deriveTitleFromUrl(dec) };
                    else if (!currentVideo.originalUrl) currentVideo.originalUrl = dec;
                    if (!currentVideo.platform) currentVideo.platform = (dec.includes('facebook.com') || dec.includes('fb.watch')) ? 'facebook' : currentVideo.platform;
                  } catch (e) {}
                }

                // Debug log
                console.log('[VideoDetails] currentVideo:', { 
                  platform: currentVideo?.platform, 
                  source: currentVideo?.source,
                  originalUrl: currentVideo?.originalUrl,
                  url: currentVideo?.url,
                  embed: currentVideo?.embed,
                  isFacebook: isFacebookVideo(currentVideo)
                });

                if (isFacebookVideo(currentVideo)) {
                  // Get the original Facebook URL for fallback
                  const originalFbUrl = currentVideo.originalUrl || currentVideo.url || (resolvedId ? decodeSafe(resolvedId) : null);
                  
                  // Prefer an explicit `embed`/`src` stored in the DB (or pluginUrl). Fall back to building a plugin URL from originalUrl.
                  const preferredSrc = currentVideo?.embed || currentVideo?.src || currentVideo?.pluginUrl || (() => {
                    const candidate = originalFbUrl || `https://www.facebook.com/${resolvedId}`;
                    const href = encodeURIComponent(candidate);
                    // Add parameters to hide related videos and enable fullscreen
                    return `https://www.facebook.com/plugins/video.php?href=${href}&show_text=false&t=0`;
                  })();

                  // If we have a stored src/pluginUrl, enhance it with additional parameters to hide related videos
                  const enhancedSrc = (() => {
                    try {
                      const url = new URL(preferredSrc);
                      // Add t=0 parameter to hide related videos overlay
                      if (!url.searchParams.has('t')) {
                        url.searchParams.set('t', '0');
                      }
                      return url.toString();
                    } catch {
                      return preferredSrc;
                    }
                  })();

                  console.log('[VideoDetails] Using Facebook iframe with src:', enhancedSrc);
                  console.log('[VideoDetails] Original Facebook URL:', originalFbUrl);

                  return (
                    <>
                      <div className="relative w-full h-full">
                        <iframe
                          title={currentVideo.title || 'Facebook video'}
                          src={enhancedSrc}
                          className="facebook-embed-iframe"
                          width="100%"
                          height="100%"
                          style={{ border: 'none', overflow: 'hidden', position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                          scrolling="no"
                          frameBorder="0"
                          allowFullScreen={true}
                          mozAllowFullScreen={true}
                          webkitAllowFullScreen={true}
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
                          referrerPolicy="origin"
                          onLoad={() => setPlayerLoading(false)}
                          onError={() => setPlayerLoading(false)}
                        />
                      </div>
                      {playerLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                          <div role="status" aria-label="Loading video" className="flex items-center justify-center">
                            <div className="w-12 h-12 border-4 rounded-full border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white animate-spin" />
                          </div>
                        </div>
                      )}
                    </>
                  );
                }

                // Non-Facebook: use ReactPlayer. Ensure resolvedId isn't an encoded URL pointing to a non-YouTube host.
                // If resolvedId contains an encoded absolute URL, decode it and prefer that as originalUrl.
                let playerUrl = currentVideo?.originalUrl;
                try {
                  if (!playerUrl && resolvedId && resolvedId.indexOf('%3A') !== -1) {
                    const dec = decodeURIComponent(resolvedId);
                    if (dec && dec.startsWith('http')) playerUrl = dec;
                  }
                } catch (e) {}

                if (!playerUrl) playerUrl = `https://www.youtube-nocookie.com/embed/${resolvedId}`;

                return (
                  <>
                    <ReactPlayer
                    ref={playerRef}
                    // prefer originalUrl when available (handles youtube watch urls), else fallback to embed by id
                    url={playerUrl}
              controls
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0 }}
              // Start playback automatically. Note: many browsers block autoplay with sound,
              // so we mute the player initially to increase the chance autoplay works.
              playing={true}
              // Controlled mute state so we can unmute on user gesture
              muted={isMuted}
              // Also set volume to 0 when muted to be extra-safe for autoplay policies
              volume={isMuted ? 0.0 : 0.8}
              // Request inline playback on iOS
                      playsinline={true}
                      onReady={() => setPlayerLoading(false)}
                      onPlay={() => setPlayerLoading(false)}
                      onBuffer={() => setPlayerLoading(true)}
                      onBufferEnd={() => setPlayerLoading(false)}
                      onError={() => setPlayerLoading(false)}
              config={{
                youtube: {
                  playerVars: {
                    autoplay: 1,              // request autoplay
                    rel: 0,                   // Don't show related videos from other channels
                    modestbranding: 1,        // Minimal YouTube branding
                    showinfo: 0,              // Don't show video info
                    iv_load_policy: 3,        // Disable annotations
                    fs: 1,                    // Allow fullscreen
                    controls: 1,              // Show player controls
                    disablekb: 0,             // Enable keyboard controls
                    playsinline: 1,           // Play inline on iOS
                    origin: window.location.origin, // Required for iframe API
                    widget_referrer: window.location.origin,
                  },
                  embedOptions: {
                    host: 'https://www.youtube-nocookie.com'
                  }
                }
              }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                    {playerLoading && (
                      <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                        <div role="status" aria-label="Loading video" className="flex items-center justify-center">
                          <div className="w-12 h-12 border-4 rounded-full border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white animate-spin" />
                        </div>
                      </div>
                    )}
                  </>
                );
            })()}

            {/* No overlay: attempting unmuted autoplay programmatically (see useEffect) */}
          </div>

          {/* Video Info Container - Add padding back for content */}
          <div className="px-4 py-3 lg:px-0">
            <div className="text-black dark:text-white font-bold text-base md:text-xl mt-4 line-clamp-2 whitespace-normal break-words">
              {displayTitle}
            </div>

            <div className="flex justify-center md:justify-end mt-4">
              <div className="flex text-black dark:text-white">
                {/* Likes / Views / Share (only for RTDB videos) */}
                {isRtdbVideo && (
                  <>
                    <button
                      onClick={async () => {
                        if (!resolvedId || likeProcessing) return;
                        setLikeProcessing(true);
                        const currentLiked = !!localStorage.getItem(`liked_${resolvedId}`);
                        const delta = currentLiked ? -1 : 1;
                        try {
                          await changeLikes(resolvedId, delta);
                          setLikesCount(prev => Math.max(0, prev + delta));
                          if (currentLiked) {
                            localStorage.removeItem(`liked_${resolvedId}`);
                          } else {
                            localStorage.setItem(`liked_${resolvedId}`, "1");
                          }
                        } catch (e) {
                          console.error("like toggle error", e);
                        } finally {
                          setLikeProcessing(false);
                        }
                      }}
                      disabled={likeProcessing}
                      title={likesCount ? `${likesCount} likes` : 'Like'}
                      className={`flex items-center h-11 px-4 rounded-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white shadow-sm ${likeProcessing ? 'opacity-50 pointer-events-none' : 'hover:shadow-md'}`}
                    >
                      <AiFillLike className="text-lg md:text-xl" />
                      <span className="ml-3 text-sm md:text-base font-medium">{abbreviateNumber(likesCount || video?.stats?.likes || 0, 2)}</span>
                      <span className="hidden sm:inline ml-2 text-sm md:text-base text-gray-600 dark:text-gray-300">Likes</span>
                    </button>

                    <div title={`${viewsCount || video?.stats?.views || 0} views`} className="flex items-center h-11 px-4 rounded-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white shadow-sm ml-4">
                      <FaEye className="text-lg md:text-xl" />
                      <span className="ml-3 text-sm md:text-base font-medium">{abbreviateNumber(viewsCount || video?.stats?.views || 0, 2)}</span>
                      <span className="hidden sm:inline ml-2 text-sm md:text-base text-gray-600 dark:text-gray-300">Views</span>
                    </div>

                    <button
                      onClick={() => {
                        if (!resolvedId) return;
                        setCopied(false);
                        setShowShare(true);
                      }}
                      title="Share"
                      className="flex items-center h-11 px-3 rounded-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white shadow-sm ml-4 hover:shadow-md"
                    >
                      <FiShare2 className="text-lg md:text-xl" />
                    </button>
                  </>
                )}

                {/* Save button: always available (supports RTDB and deep-search videos) */}
                <button
                  onClick={() => {
                    if (!resolvedId) return;

                    // prefer the current video object (may be from route state or firebase)
                    const payload = video || (firebaseVideos && firebaseVideos.find(v => v.videoId === resolvedId)) || { videoId: resolvedId, originalUrl: resolvedId };

                    if (favorited) {
                      removeFromFavorites(resolvedId);
                      setFavorited(false);
                    } else {
                      addToFavorites(payload);
                      setFavorited(true);
                    }
                  }}
                  title={favorited ? 'Saved' : 'Save'}
                  className="flex items-center h-11 px-4 rounded-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white shadow-sm ml-4 hover:shadow-md"
                >
                  {favorited ? (
                    <MdBookmark className="text-lg md:text-xl" />
                  ) : (
                    <MdBookmarkBorder className="text-lg md:text-xl" />
                  )}
                </button>
              </div>
            </div>
            </div>

            {/* Share popup/modal */}
            {showShare && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black opacity-40" onClick={() => setShowShare(false)} />
                <div className="relative bg-white dark:bg-gray-900 text-black dark:text-white rounded-lg shadow-lg p-5 w-[min(520px,90%)] z-60">
                  <div className="font-semibold text-lg mb-2">Share this link to your friend</div>
                  <button
                    onClick={() => setShowShare(false)}
                    aria-label="Close share dialog"
                    className="absolute top-3 right-3 p-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-3">
                    <input
                      readOnly
                      value={shareUrl}
                      className="flex-1 p-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(shareUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        } catch (e) {
                          console.error('copy failed', e);
                        }
                      }}
                      className="px-3 py-2 rounded bg-black text-white"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PNG banner: appears under the like/views/save section, responsive */}
            <div className="w-full flex justify-center mt-4">
              <a href="https://t.me/mywavelength_bot" target="_blank" rel="noopener noreferrer" className="w-full max-w-xl">
                <LazyImage
                  src={pndBanner}
                  alt="promo banner"
                  className="w-full object-contain rounded-md shadow-sm cursor-pointer"
                  style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                />
              </a>
            </div>

            {/* Next up then Related suggestions for mobile view (replaces More videos) */}
            {/* Hide this block on large screens because we show related videos in the right sidebar there */}
            <div className="mt-6 lg:hidden">
              {/* Next up suggestion (prominent) */}
              {Array.isArray(firebaseVideos) && firebaseVideos.length > 0 ? (
                (() => {
                  const idx = firebaseVideos.findIndex((v) => v.videoId === resolvedId);
                  const next = firebaseVideos[(idx >= 0 ? idx + 1 : 0)] || firebaseVideos[0];
                  if (!next) return null;
                  return (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-black dark:text-white mb-3">Next up</h3>
                      <VideoCard key={next.videoId || "next"} video={next} />
                    </div>
                  );
                })()
              ) : null}

              {/* Related videos (mobile) */}
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Related videos</h3>

              <div className="flex flex-col gap-4">
                {((relatedVideos === "" || !Array.isArray(relatedVideos) || relatedVideos.length === 0) || firebaseLoading) ? (
                  <ShimmerSuggestionList count={4} />
                ) : (() => {
                  // If relatedVideos is available and array, prefer that
                  if (Array.isArray(relatedVideos) && relatedVideos.length > 0) {
                    return relatedVideos.slice(0, 6).map((item, idx) => {
                      const videoObj = item?.type === "video" ? item?.video : item;
                      if (!videoObj) return null;
                      return <SuggestionVideoCard key={videoObj.videoId || idx} video={videoObj} />;
                    });
                  }
                  // Fallback: show a few from firebaseVideos (exclude current)
                  if (Array.isArray(firebaseVideos) && firebaseVideos.length > 0) {
                    const others = firebaseVideos.filter((v) => v.videoId !== resolvedId);
                    return others.slice(0, 6).map((v, idx) => (
                      <SuggestionVideoCard key={v.videoId || idx} video={v} />
                    ));
                  }
                  // If nothing, show nothing
                  return null;
                })()}
              </div>
            </div>
            {/* Desktop related videos: show under player on large screens */}
            <div className="hidden lg:block mt-8 px-4 lg:px-0">
              <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Related videos</h3>

              {((relatedVideos === "" || !Array.isArray(relatedVideos) || relatedVideos.length === 0) || firebaseLoading) ? (
                // fallback: show some other videos from firebase or shimmers
                firebaseVideos && firebaseVideos.length > 0 && !firebaseLoading ? (
                  <div className="grid grid-cols-3 gap-6">
                    {firebaseVideos.filter((v) => v.videoId !== resolvedId).slice(0, 12).map((v, index) => (
                      <div key={v.videoId || index} className="mb-0">
                        <VideoCard video={v} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="col-span-3">
                    <ShimmerSuggestionList count={9} />
                  </div>
                )
              ) : (
                (() => {
                  const entries = Array.isArray(relatedVideos) ? relatedVideos : relatedVideos?.contents || [];
                  return (
                    <div className="grid grid-cols-3 gap-6">
                      {entries.slice(0, 12).map((item, idx) => {
                        const videoObj = item?.type === "video" ? item?.video : item;
                        if (!videoObj) return null;
                        return (
                          <div key={videoObj.videoId || idx} className="mb-0">
                            <VideoCard video={videoObj} />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>

      </div>

        <div className="hidden lg:flex flex-col py-6 px-4 overflow-y-auto lg:w-[350px] xl:w-[400px] h-full">
          {/* Show shimmer while Firebase data is loading or related videos are being fetched */}
          {((relatedVideos === "" || !Array.isArray(relatedVideos) || relatedVideos.length === 0) || firebaseLoading) ? (
            // while relatedVideos is loading, show a shuffled list of home videos as temporary suggestions
            firebaseVideos && firebaseVideos.length > 0 && !firebaseLoading ? (
              (() => {
                const others = firebaseVideos.filter((v) => v.videoId !== resolvedId);
                // shuffle in-place (Fisher-Yates)
                for (let i = others.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [others[i], others[j]] = [others[j], others[i]];
                }
                return (
                  <div className="grid grid-cols-2 gap-3">
                    {others.slice(0, 15).map((v, index) => (
                      <div key={v.videoId || index} className="mb-0">
                        <VideoCard video={v} compact={true} />
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
              <div className="col-span-2">
                <ShimmerSuggestionList count={15} />
              </div>
            )
          ) : (
            (() => {
              // support both shapes: { contents: [...] } or an array
              const entries = Array.isArray(relatedVideos)
                ? relatedVideos
                : relatedVideos?.contents || [];
              return (
                <div className="grid grid-cols-2 gap-3">
                  {entries.map((item, index) => {
                    // item may be { type: 'video', video: { ... } } or already the video object
                    const videoObj = item?.type === "video" ? item?.video : item;
                    if (!videoObj) return null;
                    return (
                      <div key={index} className="mb-0">
                        <VideoCard video={videoObj} compact={true} />
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetails;
